import { ConfigurationOverride } from 'baseui/helpers/overrides';
import { Tab as RawTab, Tabs as RawTabs } from 'baseui/tabs-motion';
import * as React from 'react';
import { StyleObject } from 'styletron-standard';

import { styles } from './styles';

export type TabsItemProps = {
  title: React.ReactNode;
  key?: React.Key;
  component: React.ReactNode;
};

interface Props<T = any> {
  items: TabsItemProps[];
  activeKey: T;
  onChangeActive: (params: { activeKey: T }) => void;
  overrideStyles?: {
    tab?: ConfigurationOverride;
    tabList?: ConfigurationOverride;
    tabBorder?: ConfigurationOverride;
    tabHighlight?: ConfigurationOverride;
    tabPanel?: ConfigurationOverride;
  };
}

export default function Tabs<T>(props: Props<T>) {
  const { items, activeKey, onChangeActive, overrideStyles } = props;

  const overrides = (index: React.Key, style?: StyleObject) => ({
    overrides: {
      TabPanel: {
        style:
          activeKey === index
            ? {
                ...(styles.tabPanel as any),
                ...overrideStyles?.tabPanel,
                ...style,
              }
            : { ...style, ...overrideStyles?.tabPanel },
      },
    },
  });

  return (
    <RawTabs
      activeKey={activeKey as React.Key}
      onChange={(params) => {
        onChangeActive?.(params as any);
      }}
      overrides={{
        Root: {
          style: {
            ...(styles.tab as any),
            ...overrideStyles?.tab,
            height: 'auto',
          },
        },
        TabList: {
          style: { ...styles.tabList, ...overrideStyles?.tabList },
        },
        TabBorder: {
          style: { ...styles.tabBorder, ...overrideStyles?.tabBorder },
        },
        TabHighlight: {
          style: { ...styles.tabHighlight, ...overrideStyles?.tabHighlight },
        },
      }}
      activateOnFocus
    >
      {items.map((item, index) => (
        <RawTab
          key={item.key ?? index.toString()}
          {...overrides(item.key ?? index.toString())}
          title={item.title}
        >
          {item.component}
        </RawTab>
      ))}
    </RawTabs>
  );
}
